import { useState, ChangeEvent, SyntheticEvent } from 'react';
import Header from '../component/Header';
import Footer from '../component/footer';
import { CheckCircle2, ArrowLeft, UserMinus } from 'lucide-react';
import { unsubscribe as unsubscribeApi } from '../services/subscriptionService';
import Seo from '../components/Seo';

const Unsubscribe = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    iban: ''
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.email || !formData.firstName || !formData.lastName || !formData.iban) {
      setErrorMessage('Please fill out all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        iban_last_5: formData.iban,
      };

      await unsubscribeApi(payload);

      setIsSubmitted(true);
    } catch (err: any) {
      setErrorMessage(err?.message || 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Unsubscribe & Manage Subscription | LearnWebAcademy"
        description="Manage your subscription preferences and unsubscribe easily with a simple process designed for a smooth user experience."
        canonicalPath="/unsubscribe"
      />
      <Header />
      <div className="min-h-screen flex flex-col bg-[#F4F5F7] font-sans">
        

        {/* Main Content - Centered Card Layout */}
      <main className="relative flex items-center justify-center flex-grow px-6 py-24 overflow-hidden md:px-12">
        
        {/* Background decorative elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-gradient-to-b from-[#4CE470]/10 to-transparent rounded-full blur-3xl -z-10 pointer-events-none"></div>

        <div className="bg-white max-w-2xl w-full rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 md:p-12 text-center relative z-10">
          
          {!isSubmitted ? (
            /* --- Form View --- */
            <>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 text-red-500 border border-red-100 shadow-sm bg-red-50 rounded-2xl">
                <UserMinus size={32} />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#113A28] mb-6 tracking-tight">
                Unsubscribe
              </h1>
              
              <div className="mb-10 space-y-4">
                <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                  To cancel your subscription, simply enter your details below. Your subscription will be cancelled immediately, and you will not be charged again.
                </p>
                <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                  If you need assistance or have any questions, feel free to contact our support team at{' '}
                  <a href="mailto:support@learnwebacademy.net" className="text-[#4CE470] font-bold hover:underline">
                    support@learnwebacademy.net
                  </a>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 text-left">
                {errorMessage && (
                  <div className="p-3 mb-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg">
                    {errorMessage}
                  </div>
                )}
                
                {/* Name Row */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block mb-2 ml-1 text-xs font-bold tracking-wider text-gray-700 uppercase">
                      First name*
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#4CE470] focus:ring-4 focus:ring-[#4CE470]/10 transition-all text-gray-800"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block mb-2 ml-1 text-xs font-bold tracking-wider text-gray-700 uppercase">
                      Last name*
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#4CE470] focus:ring-4 focus:ring-[#4CE470]/10 transition-all text-gray-800"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block mb-2 ml-1 text-xs font-bold tracking-wider text-gray-700 uppercase">
                    Email*
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#4CE470] focus:ring-4 focus:ring-[#4CE470]/10 transition-all text-gray-800"
                  />
                </div>

                {/* IBAN Field */}
                <div>
                  <label htmlFor="iban" className="block mb-2 ml-1 text-xs font-bold tracking-wider text-gray-700 uppercase">
                    Last 5 Digits Of IBAN*
                  </label>
                  <input
                    type="text"
                    id="iban"
           
                    value={formData.iban}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 12345"
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#4CE470] focus:ring-4 focus:ring-[#4CE470]/10 transition-all text-gray-800"
                  />
                </div>

                {/* Actions */}
                <div className="pt-6 space-y-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase transition-all ${isLoading ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-[#113A28] text-white hover:bg-[#1a573c] hover:shadow-lg hover:-translate-y-0.5'}`}
                  >
                    {isLoading ? 'SENDING...' : 'SEND'}
                  </button>
                  
                  <a 
                    href="/" 
                    className="block w-full text-center py-4 text-sm font-bold text-gray-500 hover:text-[#113A28] transition-colors"
                  >
                    Cancel, keep me subscribed
                  </a>
                </div>
              </form>
            </>
          ) : (
            /* --- Success View --- */
            <div className="py-8">
              <div className="w-24 h-24 bg-[#4CE470]/10 text-[#4CE470] rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={48} />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#113A28] mb-6 tracking-tight">
                Successfully Unsubscribed
              </h2>
              
              <p className="mb-10 text-base leading-relaxed text-gray-600">
                <strong>{formData.email}</strong> has been cancelled. Your subscription is terminated immediately, and you will not be charged again.
              </p>

              <a 
                href="/" 
                className="inline-flex items-center justify-center gap-2 bg-gray-50 text-[#113A28] border border-gray-200 px-8 py-4 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors w-full md:w-auto"
              >
                <ArrowLeft size={18} />
                Return to Homepage
              </a>
            </div>
          )}

        </div>
      </main>

    
      </div>
      <Footer />
    </>
  );
};

export default Unsubscribe;
